import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useQuery } from '@tanstack/react-query';
import { 
  Search, 
  Users, 
  Building2, 
  Calendar, 
  BarChart3, 
  FolderKanban,
  Calculator,
  Activity,
  MapPin,
  Battery,
  CheckSquare,
  ClipboardList,
  Settings,
  Bell,
  AlertTriangle,
  LayoutTemplate,
  Gauge,
  Sun,
  Moon,
  LogOut,
  PlusCircle,
  Settings as SettingsIcon,
  GitBranch,
  Zap,
  Target,
  Construction,
  Plug,
  Briefcase,
  Crosshair,
  Heart,
  TrendingUp,
  Mic,
  FileText,
  Beaker,
  Truck,
  HandCoins,
  Map,
  Mail,
  Archive,
  Ticket,
  BookOpen,
  FlaskConical,
  Globe,
  Lightbulb,
  StickyNote,
  Download,
  Presentation,
} from 'lucide-react';
import { getStageConfig } from '@/components/crm/project/projectUtils';
import { getChargingStageLabel } from '@/lib/stageLabels';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/useProjects';
import { supabase } from '@/integrations/supabase/client';

export function GlobalCommand() {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { hasRole, signOut } = useAuth();
  const { projects } = useProjects();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    // De zijbalk opent de zoekbalk met dit event (knop "Zoeken"), zonder eigen state te delen.
    const openen = () => setOpen(true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('crm:zoeken', openen);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('crm:zoeken', openen);
    };
  }, []);

  const handleSelect = (path: string) => {
    setOpen(false);
    setSearchTerm('');
    setDebouncedSearch('');
    navigate(path);
  };

  const navigationItems = [
    { icon: BarChart3, label: 'Dashboard', href: '/crm', keywords: 'home overzicht' },
    { icon: Bell, label: 'Notificaties', href: '/crm/notifications', keywords: 'meldingen alerts' },
    { icon: Mail, label: 'Mail-triage', href: '/crm/mail', keywords: 'mail email post triage inbox suggesties koppelen' },
    { icon: Calendar, label: 'Agenda', href: '/crm/agenda', keywords: 'calendar afspraken' },
    { icon: ClipboardList, label: 'Intakes', href: '/crm/intakes', keywords: 'intake intakes externe aanvraag formulier ingediend klantgegevens' },
    { icon: Building2, label: 'Organisaties', href: '/crm/organisaties', keywords: 'bedrijven klanten' },
    { icon: Users, label: 'Contacten', href: '/crm/contacts', keywords: 'personen mensen' },
    { icon: FolderKanban, label: 'Projecten', href: '/crm/projects', keywords: 'opdrachten taken' },
    { icon: Calculator, label: 'Businesscases', href: '/crm/businesscases', keywords: 'berekeningen roi' },
    { icon: Map, label: 'Project Builder', href: '/project-builder', keywords: 'project builder laadplein designer v2 tekenen site planner kavel scenario block terreinplan tekenaar' },
    { icon: Activity, label: 'Flex-E Simulaties', href: '/crm/flexe', keywords: 'simulatie energie batterij overzicht' },
    { icon: PlusCircle, label: 'Nieuwe Flex-E Simulatie', href: '/crm/flexe/new', keywords: 'simulatie maken nieuw toevoegen' },
    { icon: SettingsIcon, label: 'Flex-E Machines', href: '/crm/flexe/machines', keywords: 'machines catalogus apparaten' },
    { icon: GitBranch, label: 'Flex-E Scenario\'s', href: '/crm/flexe/scenarios', keywords: 'scenario\'s templates what-if' },
    // F9.12: de energiemonitor staat op de sitepagina; Locaties is de ingang.
    { icon: MapPin, label: 'Locaties', href: '/crm/sites', keywords: 'adressen vestigingen sites installaties energiemonitoring ems monitoring meetdata' },
    { icon: Battery, label: 'Battery Planner', href: '/crm/battery-planner', keywords: 'batterijen opslag' },
    { icon: CheckSquare, label: "To-do's", href: '/crm/todos', keywords: 'taken acties' },
    { icon: Settings, label: 'Instellingen', href: '/crm/settings', keywords: 'configuratie setup' },
    // Charging items
    { icon: Zap, label: 'Charging Dashboard', href: '/crm/charging', keywords: 'laadpalen laadlocaties charging' },
    { icon: Target, label: 'Charging Acquisitie', href: '/crm/charging/acquisitie', keywords: 'laadpalen acquisitie nieuw' },
    { icon: Construction, label: 'Charging Realisatie', href: '/crm/charging/realisatie', keywords: 'laadpalen bouw realisatie' },
    { icon: Plug, label: 'Charging Operatie', href: '/crm/charging/operatie', keywords: 'laadpalen operationeel' },
    { icon: Briefcase, label: 'Opportunities', href: '/crm/charging/opportunities', keywords: 'partnerships transporteurs' },
    { icon: Crosshair, label: 'Concurrentie', href: '/crm/charging/concurrentie', keywords: 'competitors leap24 milence' },
    { icon: Heart, label: 'Wensen', href: '/crm/charging/wensen', keywords: 'klant wensen gewenste locaties' },
    // Analyses items
    { icon: TrendingUp, label: 'Analyses', href: '/crm/analyses', keywords: 'analyses overzicht' },
    { icon: Zap, label: 'Piekvermogen', href: '/crm/analyses/piekvermogen', keywords: 'piek netbelasting' },
    { icon: Mic, label: 'Transcripties', href: '/crm/analyses/transcripties', keywords: 'audio transcriptie spraak' },
    // T4.2, besluit van Stan 12-09-2026: EPSE, Hub en Hub V2 uit het menu en
    // uit dit palet — trap 1 van de sloopladder. De routes en de data blijven;
    // dit is één regel terugzetten. De EPSE-sites zijn hieronder nog wél
    // doorzoekbaar (dat is geen menu-ingang maar een zoekresultaat).
    //   { icon: Battery, label: 'EPSE Simulatie', href: '/crm/analyses/epse', keywords: 'energie batterij simulatie bess' },
    //   { icon: Beaker, label: 'Hub V2 Simulatie', href: '/crm/analyses/hub-v2', keywords: 'hub v2 simulatie energiepark test' },
    { icon: Truck, label: 'Laadplein Intelligence', href: '/crm/laadplein', keywords: 'laadplein vrachtwagen truck charging marktonderzoek demand corridors spula' },
    { icon: HandCoins, label: 'Subsidies', href: '/crm/subsidies', keywords: 'subsidies spula sprila flex-e aanzet eia mia vamil regelingen' },
    // Doorlopen op 05-09-2026: 16 bestemmingen hadden wel een route en meestal
    // ook een sidebar-item, maar geen ingang in de palette. Offertes was de
    // opvallendste - een kernscherm dat je met Cmd-K niet kon vinden.
    { icon: FileText, label: 'Offertes', href: '/crm/quotes', keywords: 'offertes quotes aanbieding prijsopgave voorstel' },
    { icon: LayoutTemplate, label: 'Offerte-templates', href: '/crm/quote-templates', keywords: 'offerte template sjabloon pdf opmaak' },
    { icon: Archive, label: 'Archief', href: '/crm/archief', keywords: 'archief gearchiveerd verwijderd herstellen prullenbak' },
    { icon: Ticket, label: 'Tickets', href: '/crm/tickets', keywords: 'tickets support meldingen vragen' },
    { icon: BookOpen, label: 'Documentatie', href: '/crm/documentatie', keywords: 'documentatie handleiding uitleg docs' },
    // Zie de noot hierboven: ook deze ingang is per 12-09-2026 uitgezet.
    //   { icon: Beaker, label: 'Hub Simulatie', href: '/crm/analyses/hub', keywords: 'hub simulatie energiepark analyse' },
    { icon: FlaskConical, label: 'Sandbox', href: '/crm/sandbox', keywords: 'sandbox test experiment proeftuin' },
    { icon: Globe, label: 'Website', href: '/crm/website', keywords: 'website site online marketing bezoek bezoekers formulieren leads signalen overzicht' },
    { icon: CheckSquare, label: 'Website planning', href: '/crm/website/planning', keywords: 'website planning taken notities ideeen concepten' },
    { icon: Globe, label: 'Website: content op de site', href: '/crm/website/site', keywords: 'website site content nieuws kennisbank vacatures projectverhalen faq teksten publiceren greenvision.energy' },
    { icon: FileText, label: 'Website tekstconcepten', href: '/crm/website/content', keywords: 'website concepten teksten planning' },
    { icon: CheckSquare, label: 'Website taken', href: '/crm/website/taken', keywords: 'website taken todo werk' },
    { icon: Lightbulb, label: 'Website ideeen', href: '/crm/website/ideeen', keywords: 'website ideeen idee brainstorm' },
    { icon: StickyNote, label: 'Website notities', href: '/crm/website/notities', keywords: 'website notities aantekeningen' },
    { icon: Archive, label: 'Charging Archief', href: '/crm/charging/archief', keywords: 'charging archief laadlocaties gearchiveerd' },
    { icon: Download, label: 'Charging Export', href: '/crm/charging/export', keywords: 'charging export download csv excel' },
    { icon: BarChart3, label: 'Gauw-tabel', href: '/crm/charging/gauw-tabel', keywords: 'gauw tabel charging overzicht matrix' },
    { icon: Presentation, label: 'Charging Presentatie', href: '/crm/charging/presentatie', keywords: 'charging presentatie slides pitch' },
    { icon: CheckSquare, label: "Charging To-do's", href: '/crm/charging/todos', keywords: 'charging taken todo acties' },
  ];

  const adminItems = hasRole('super_admin') ? [
    { icon: Users, label: 'CRM Gebruikers', href: '/crm/users', keywords: 'medewerkers team' },
    { icon: Users, label: 'Portal Gebruikers', href: '/crm/portal-users', keywords: 'klanten toegang' },
    { icon: AlertTriangle, label: 'Foutmeldingen', href: '/crm/error-reports', keywords: 'errors bugs' },
    { icon: Gauge, label: 'Mail en AI', href: '/crm/mail-en-ai', keywords: 'kosten tokens gemini taalmodel nats mailstatus aanlevering draadoordeel beheer' },
    { icon: LayoutTemplate, label: 'Project Templates', href: '/crm/project-templates', keywords: 'sjablonen templates' }
  ] : [];

  const recentProjects = projects.slice(0, 5);

  // Search queries
  const { data: searchOrganizations = [] } = useQuery({
    queryKey: ['search-organizations', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('organizations')
        .select('id, name, classification, status, favicon_url, logo_url')
        .is('deleted_at', null)
        .ilike('name', `%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  const { data: searchContacts = [] } = useQuery({
    queryKey: ['search-contacts', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, org_id, organizations(name)')
        .is('deleted_at', null)
        .or(`first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%,email.ilike.%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  const { data: searchBusinesscases = [] } = useQuery({
    queryKey: ['search-businesscases', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('businesscases')
        .select('id, name, project_id, projects(name)')
        .eq('is_archived', false)
        .ilike('name', `%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  const { data: searchSimulations = [] } = useQuery({
    queryKey: ['search-simulations', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('flexe_simulations')
        .select('id, name, created_at')
        .ilike('name', `%${debouncedSearch}%`)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  const { data: searchSites = [] } = useQuery({
    queryKey: ['search-sites', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('sites')
        .select('id, name, address, site_type')
        .is('deleted_at', null)
        .or(`name.ilike.%${debouncedSearch}%,address.ilike.%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // NEW: Charging Locations search
  const { data: searchChargingLocations = [] } = useQuery({
    queryKey: ['search-charging-locations', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('charging_locations')
        .select('id, name, city, stage, tags')
        .is('deleted_at', null)
        .or(`name.ilike.%${debouncedSearch}%,city.ilike.%${debouncedSearch}%,address.ilike.%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // NEW: Opportunities search
  const { data: searchOpportunities = [] } = useQuery({
    queryKey: ['search-opportunities', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('charging_opportunities')
        .select('id, name, status, org_id, organizations(name)')
        .is('deleted_at', null)
        .ilike('name', `%${debouncedSearch}%`)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // NEW: EPSE Sites search
  const { data: searchEPSESites = [] } = useQuery({
    queryKey: ['search-epse-sites', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('epse_sites')
        .select('id, name, connection_kw_limit, created_at')
        .ilike('name', `%${debouncedSearch}%`)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // NEW: Transcriptions search
  const { data: searchTranscriptions = [] } = useQuery({
    queryKey: ['search-transcriptions', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('transcriptions')
        .select('id, file_name, status, created_at')
        .ilike('file_name', `%${debouncedSearch}%`)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  // NEW: To-dos search
  const { data: searchTodos = [] } = useQuery({
    queryKey: ['search-todos', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return [];
      const { data } = await supabase
        .from('unified_todos')
        .select('id, title, status, priority, due_date')
        .ilike('title', `%${debouncedSearch}%`)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearch.length >= 2,
  });

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-full justify-start rounded-md bg-muted/50 text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden lg:inline-flex">Zoeken...</span>
        <span className="inline-flex lg:hidden">Zoek</span>
        <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-6 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">{isMac ? '⌘' : 'Ctrl'}</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput 
          placeholder="Zoek door CRM..." 
          className="h-11"
          value={searchTerm}
          onValueChange={setSearchTerm}
        />
        <CommandList className="max-h-[450px]">
          <CommandEmpty>
            {debouncedSearch.length < 2 
              ? 'Typ minimaal 2 karakters om te zoeken...' 
              : 'Geen resultaten gevonden.'}
          </CommandEmpty>

          {/* Navigatie Groep */}
          <CommandGroup heading="Navigatie">
            {navigationItems.map((item) => (
              <CommandItem
                key={item.href}
                value={`${item.label} ${item.keywords}`}
                onSelect={() => handleSelect(item.href)}
                className="flex items-center gap-2"
              >
                <item.icon className="h-4 w-4 text-muted-foreground" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          {/* Admin Navigatie */}
          {adminItems.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Admin">
                {adminItems.map((item) => (
                  <CommandItem
                    key={item.href}
                    value={`${item.label} ${item.keywords}`}
                    onSelect={() => handleSelect(item.href)}
                    className="flex items-center gap-2"
                  >
                    <item.icon className="h-4 w-4 text-muted-foreground" />
                    <span>{item.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Charging Locaties */}
          {debouncedSearch.length >= 2 && searchChargingLocations.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Charging Locaties">
                {searchChargingLocations.map((loc: any) => (
                  <CommandItem
                    key={loc.id}
                    value={`charging-${loc.id}-${loc.name}`}
                    onSelect={() => handleSelect(`/crm/charging/locatie/${loc.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{loc.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{loc.city || 'Geen stad'} • {getChargingStageLabel(loc.stage)}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Laadlocatie</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Opportunities */}
          {debouncedSearch.length >= 2 && searchOpportunities.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Opportunities">
                {searchOpportunities.map((opp: any) => (
                  <CommandItem
                    key={opp.id}
                    value={`opportunity-${opp.id}-${opp.name}`}
                    onSelect={() => handleSelect(`/crm/charging/opportunities`)}
                    className="flex items-center gap-2"
                  >
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{opp.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{opp.organizations?.name || 'Geen org'} • {opp.status}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Opportunity</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - EPSE Sites */}
          {debouncedSearch.length >= 2 && searchEPSESites.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="EPSE Sites">
                {searchEPSESites.map((site: any) => (
                  <CommandItem
                    key={site.id}
                    value={`epse-${site.id}-${site.name}`}
                    onSelect={() => handleSelect(`/crm/analyses/epse/${site.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Battery className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{site.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{site.connection_kw_limit} kW limiet</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">EPSE</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Transcripties */}
          {debouncedSearch.length >= 2 && searchTranscriptions.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Transcripties">
                {searchTranscriptions.map((t: any) => (
                  <CommandItem
                    key={t.id}
                    value={`transcription-${t.id}-${t.file_name}`}
                    onSelect={() => handleSelect(`/crm/analyses/transcripties`)}
                    className="flex items-center gap-2"
                  >
                    <Mic className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{t.file_name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{t.status} • {new Date(t.created_at).toLocaleDateString('nl-NL')}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Transcriptie</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - To-do's */}
          {debouncedSearch.length >= 2 && searchTodos.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="To-do's">
                {searchTodos.map((todo: any) => (
                  <CommandItem
                    key={todo.id}
                    value={`todo-${todo.id}-${todo.title}`}
                    onSelect={() => handleSelect(`/crm/todos`)}
                    className="flex items-center gap-2"
                  >
                    <CheckSquare className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{todo.title}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{todo.status} • {todo.priority || 'normal'}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">To-do</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Organisaties */}
          {debouncedSearch.length >= 2 && searchOrganizations.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Organisaties">
                {searchOrganizations.map((org: any) => (
                  <CommandItem
                    key={org.id}
                    value={`org-${org.id}-${org.name}`}
                    onSelect={() => handleSelect(`/crm/organisaties/${org.id}`)}
                    className="flex items-center gap-2"
                  >
                    {(org.logo_url || org.favicon_url) ? (
                      <img 
                        src={org.logo_url || org.favicon_url} 
                        alt=""
                        className="h-4 w-4 object-contain flex-shrink-0"
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                    ) : (
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{org.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{org.classification} • {org.status}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Organisatie</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Contacten */}
          {debouncedSearch.length >= 2 && searchContacts.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Contacten">
                {searchContacts.map((contact: any) => (
                  <CommandItem
                    key={contact.id}
                    value={`contact-${contact.id}-${contact.first_name}-${contact.last_name}-${contact.email}`}
                    onSelect={() => handleSelect(`/crm/contacts/${contact.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">
                        {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || 'Naamloos'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{contact.organizations?.name || 'Geen organisatie'} • {contact.email}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Contact</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Projecten */}
          {debouncedSearch.length >= 2 && projects.filter((p: any) => 
            p.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
          ).slice(0, 5).length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Projecten">
                {projects.filter((p: any) => 
                  p.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
                ).slice(0, 5).map((project: any) => (
                  <CommandItem
                    key={project.id}
                    value={`project-${project.id}-${project.name}`}
                    onSelect={() => handleSelect(`/crm/projects/${project.id}`)}
                    className="flex items-center gap-2"
                  >
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{project.name || 'Naamloos Project'}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{getStageConfig(project.stage).label} • {new Date(project.created_at).toLocaleDateString('nl-NL')}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Project</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Businesscases */}
          {debouncedSearch.length >= 2 && searchBusinesscases.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Businesscases">
                {searchBusinesscases.map((bc: any) => (
                  <CommandItem
                    key={bc.id}
                    value={`businesscase-${bc.id}-${bc.name}`}
                    onSelect={() => handleSelect(`/crm/businesscases/${bc.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Calculator className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{bc.name || 'Naamloos'}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{bc.projects?.name || 'Geen project'}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Businesscase</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Flex-E Simulaties */}
          {debouncedSearch.length >= 2 && searchSimulations.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Flex-E Simulaties">
                {searchSimulations.map((sim: any) => (
                  <CommandItem
                    key={sim.id}
                    value={`simulation-${sim.id}-${sim.name}`}
                    onSelect={() => handleSelect(`/crm/flexe/${sim.id}`)}
                    className="flex items-center gap-2"
                  >
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{sim.name || 'Naamloze simulatie'}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{new Date(sim.created_at).toLocaleDateString('nl-NL')}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Simulatie</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Zoekresultaten - Locaties */}
          {debouncedSearch.length >= 2 && searchSites.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Locaties">
                {searchSites.map((site: any) => (
                  <CommandItem
                    key={site.id}
                    value={`site-${site.id}-${site.name}-${site.address}`}
                    onSelect={() => handleSelect(`/crm/sites/${site.id}`)}
                    className="flex items-center gap-2"
                  >
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{site.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{site.address || 'Geen adres'} • {site.site_type}</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0">Locatie</Badge>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Recente Projecten */}
          {!debouncedSearch && recentProjects.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Recente Projecten">
                {recentProjects.map((project: any) => (
                  <CommandItem
                    key={project.id}
                    value={`recent-project-${project.id}`}
                    onSelect={() => handleSelect(`/crm/projects/${project.id}`)}
                    className="flex items-center gap-2"
                  >
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{project.name || 'Naamloos'}</p>
                      <p className="text-xs text-muted-foreground truncate">{getStageConfig(project.stage).label}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Acties */}
          <CommandSeparator />
          <CommandGroup heading="Acties">
            <CommandItem
              value="thema wisselen dark light mode"
              onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="flex items-center gap-2"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground" />
              )}
              <span>Thema wisselen</span>
            </CommandItem>
            <CommandItem
              value="uitloggen logout"
              onSelect={() => signOut()}
              className="flex items-center gap-2 text-destructive"
            >
              <LogOut className="h-4 w-4" />
              <span>Uitloggen</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
